import type { InMemoryCache } from '@apollo/client';
import { ApolloLink, gql, makeVar } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { generatePersistedQueryIdsFromManifest } from '@apollo/persisted-query-lists';
import * as Sentry from '@sentry/react';
import _ from 'lodash';

import { readFromLocalStorage } from 'src/hooks/useLocalStorage';

import { absoluteURL } from '../absoluteURL';
import { trackUserIsLoggedIn } from '../analytics';
import Config from '../config';
import { StudioConfigEnv, runtimeConfig } from '../config/runtime';
import { goLogout } from '../goLogout';
import type { GraphQLTypes } from '../graphqlTypes';
import { notProduction } from '../notProduction';
import { locationToPath } from '../routeConfig/locationToPath';
import { mergeQueryParams } from '../routing';

import type { CatchErrorsContext } from './catchErrors';
import {
  catchErrors,
  isExpiredSessionError,
  isInvalidApiKeyError,
  isMissingSessionError,
} from './catchErrors';
import { sha256 } from './hash';
import type { SentryReportingContext } from './sentryErrorReporting';
import { sentryErrorReporting } from './sentryErrorReporting';

// Alias this for now, but the idea is to grow the feature set of things
// we store in the context here.
type AppLinkContext = CatchErrorsContext & SentryReportingContext;

// Convenience method / syntax sugar for bootstrapping an AppLinkContext
export const appLinkContext = (...contexts: AppLinkContext[]): AppLinkContext =>
  Object.assign({}, ...contexts);

const checkLogin = new ApolloLink((operation, forward) =>
  forward(operation).map((result) => {
    const { response, cache } = operation.getContext() as {
      response: Response;
      cache: InMemoryCache;
    };
    const isLoggedIn = response.headers.get('logged-in');
    trackUserIsLoggedIn(isLoggedIn === 'true');
    if (isLoggedIn && (isLoggedIn === 'true' || isLoggedIn === 'false')) {
      cache.writeQuery<GraphQLTypes.IsLoggedInInitialCacheSeedQuery>({
        query: gql`
          query IsLoggedInInitialCacheSeedQuery {
            isLoggedIn @client
          }
        `,
        data: {
          isLoggedIn: isLoggedIn === 'true',
        },
      });
    }

    return result;
  }),
);

const alreadyHasExpiredSession = makeVar(false);

const checkSessionExpirationLink = onError(({ graphQLErrors }) => {
  // If the backend says that the user's session is expired, we redirect
  // them to '/logout'. This page performs cleanup on the frontend (memory and
  // localstorage), and then makes a call to the backend logout endpoint.
  const sessionIsNowExpired =
    graphQLErrors &&
    graphQLErrors.some((gqlError) => isExpiredSessionError(gqlError));

  const sessionIsMissingCredential =
    graphQLErrors &&
    graphQLErrors.some(
      (gqlError) =>
        isMissingSessionError(gqlError) || isInvalidApiKeyError(gqlError),
    );

  // We only want to set the location to '/logout' once, so we use (misuse?) apollo-client's
  // reactive vars for checking the state
  if (
    (sessionIsNowExpired || sessionIsMissingCredential) &&
    !alreadyHasExpiredSession()
  ) {
    alreadyHasExpiredSession(true);
    const callbackUrl = sessionIsNowExpired
      ? absoluteURL(
          locationToPath({
            ...window.location,
            search: mergeQueryParams(window.location.search, {
              sessionError: 1,
            }),
          }),
        )
      : window.location.href;
    goLogout({ callbackUrl });
  }
});

const makeOperationFrequencyMonitor = () => {
  const operationsThreshold = 150;
  const intervalMs = 60 * 1000;
  const ignoreFrequencyFilter = new Set([
    'AddExistingGraphFormContentGraphIDAvailableQuery',
    'PublishToNewGraphModalGraphIDAvailableQuery',
    'SupergraphsVariantsTableQuery', // this query is once per variant,
    // graphs may have many graphs w many variants
    'UsePermissionsServiceQuery', // this query is in components
    // that are rendered as lists, like variant items in the supergraph list,
    // so for orgs with many variants it may fire 100+ times in a single page load
    'GraphsListVariantItemDruidStats',
  ]);
  let operationsInPeriod: string[] = [];
  const monitorIntervalId = setInterval(() => {
    if (operationsInPeriod.length > operationsThreshold) {
      Sentry.captureMessage(
        `Excessive operations occurred in ${intervalMs}ms. This is probably unexpected.`,
        {
          level: 'warning',
          extra: {
            operationCount: operationsInPeriod.length,
            operations: _.countBy(operationsInPeriod),
          },
        },
      );
      // stop the monitor once triggered; it'll likely be triggered every minute if it's triggered once
      clearInterval(monitorIntervalId);
    }
    operationsInPeriod = [];
  }, intervalMs);

  return new ApolloLink((operation, forward) => {
    if (!ignoreFrequencyFilter.has(operation.operationName) && !notProduction) {
      operationsInPeriod.push(operation.operationName);
    }
    return forward(operation);
  });
};

const sudoLink = setContext(() => ({
  headers: { 'apollo-sudo': readFromLocalStorage('sudo') },
}));

export const chain: ApolloLink[] = [
  sudoLink,
  sentryErrorReporting,
  catchErrors,
  checkLogin,
  checkSessionExpirationLink,
  makeOperationFrequencyMonitor(),
];

async function loadManifest() {
  // Don't prefetch, because we only need this in staging!
  return import(
    /* webpackChunkName: "persistedQueryManifest" */ './persistedQueryManifest.json'
  );
}

if (Config.settings.enablePersistedQueries) {
  // If we're running a proper build on staging (but not, say, `npm start
  // staging`, then send only persisted queries (not freeform GraphQL)!
  // The manifests are published when we deploy.
  if (
    window.location.hostname === 'studio-staging.apollographql.com' &&
    runtimeConfig.env === StudioConfigEnv.Staging
  ) {
    chain.push(
      createPersistedQueryLink({
        ...generatePersistedQueryIdsFromManifest({ loadManifest }),
        useGETForHashedQueries:
          // XXX: `useGETForHashedQueries: true` results in 413 errors for
          // services that have large numbers of clients (ie, we need to avoid
          // using GETs when variables are large).
          false,
      }),
    );
  } else if (crypto?.subtle) {
    // Otherwise enable automatic persisted queries on test, staging and
    // production.
    chain.push(
      createPersistedQueryLink({
        sha256,
        useGETForHashedQueries:
          // XXX: `useGETForHashedQueries: true` results in 413 errors
          // for services that have large numbers of clients.
          false,
      }),
    );
  }
}
